import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { useShippingPrice } from '../helpers/useShippingPrice.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles.scss';

import { type ShipsFrom_viewer$key } from './__generated__/ShipsFrom_viewer.graphql.ts';
import { type ShipsFrom_item$key } from './__generated__/ShipsFrom_item.graphql.ts';

const viewerFragment = graphql`
    fragment ShipsFrom_viewer on Viewer
    @argumentDefinitions(
        fetchRegionalInfo: { type: "Boolean", defaultValue: false }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        ...useShippingPrice_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

const itemFragment = graphql`
    fragment ShipsFrom_item on Item
    @argumentDefinitions(
        buyerId: { type: "String", defaultValue: "" }
        fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
        fetchLiveShipmentQuote: { type: "Boolean", defaultValue: true }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        address {
            displayCountry
        }
        ...useShippingPrice_item
            @arguments(
                buyerId: $buyerId
                fetchShippingCostInfo: $fetchShippingCostInfo
                fetchLiveShipmentQuote: $fetchLiveShipmentQuote
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

type Props = {
    viewer: ShipsFrom_viewer$key | null | undefined;
    item: ShipsFrom_item$key;
    currency: string;
};

export const ShipsFrom: FC<Props> = ({ viewer: viewerRef, item: itemRef, currency }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const amount = useShippingPrice({ item, viewer, currency });
    const country = item.address?.displayCountry;
    const isFreeShipping = amount === 0;

    if (!country) {
        return null;
    }

    return (
        <div
            className={classnames(
                dibsCss.sassyFontBodyXSmall,
                dibsCss.textNoir,
                dibsCss.mbXsmall,
                styles.negativeMarginTop
            )}
        >
            <FormattedMessage
                id="dc.searchProductTile.shipsFrom"
                defaultMessage={
                    '{isFreeShipping, select, true {<heavyGreen>Free Shipping</heavyGreen>} other {Ships}} from {country}'
                }
                values={{
                    country,
                    isFreeShipping,
                    heavyGreen: content => (
                        <span
                            className={classnames(
                                dibsCss.sassyFontBodyXSmallHeavy,
                                dibsCss.textSuccesssecondary
                            )}
                        >
                            {content}
                        </span>
                    ),
                }}
            />
        </div>
    );
};
